import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Save, Plus, Trash2, ChevronUp, ChevronDown, BookOpen } from "lucide-react";
import { toast } from "@/lib/toastBanner";
import RichTextEditor from "@/components/RichTextEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CohortWelcomeTab({ cohortId }) {
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [welcomeLexps, setWelcomeLexps] = useState([]);
  const [allLexps, setAllLexps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedLexpId, setSelectedLexpId] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [welcomeRes, lexpsRes] = await Promise.all([
        axios.get(`${API}/cohorts/${cohortId}/welcome`, { withCredentials: true }),
        axios.get(`${API}/learning-experiences`, { withCredentials: true }),
      ]);
      setWelcomeMessage(welcomeRes.data.welcome_message || "");
      setWelcomeLexps(welcomeRes.data.welcome_lexps || []);
      setAllLexps(lexpsRes.data || []);
    } catch { toast.error("Failed to load welcome config"); }
    finally { setLoading(false); }
  }, [cohortId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/cohorts/${cohortId}/welcome`, {
        welcome_message: welcomeMessage,
        welcome_lexps: welcomeLexps.map((l) => l.lexp_id),
      }, { withCredentials: true });
      toast.success("Welcome settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const addLexp = () => {
    if (!selectedLexpId) return;
    if (welcomeLexps.some((l) => l.lexp_id === selectedLexpId)) {
      toast.error("Already added");
      return;
    }
    const lexp = allLexps.find((l) => l.id === selectedLexpId);
    if (!lexp) return;
    setWelcomeLexps([...welcomeLexps, { lexp_id: lexp.id, title: lexp.title }]);
    setSelectedLexpId("");
  };

  const removeLexp = (lexpId) => {
    setWelcomeLexps(welcomeLexps.filter((l) => l.lexp_id !== lexpId));
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const arr = [...welcomeLexps];
    [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
    setWelcomeLexps(arr);
  };

  const moveDown = (idx) => {
    if (idx === welcomeLexps.length - 1) return;
    const arr = [...welcomeLexps];
    [arr[idx + 1], arr[idx]] = [arr[idx], arr[idx + 1]];
    setWelcomeLexps(arr);
  };

  // Available LEXPs = all minus already added
  const availableLexps = allLexps.filter((l) => !welcomeLexps.some((wl) => wl.lexp_id === l.id));

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cohort-welcome-tab">
      {/* Welcome Message */}
      <div className="space-y-2">
        <Label className="text-[#A1A9B8] font-body text-sm">Custom Welcome Message</Label>
        <div className="rounded-lg border border-white/10 overflow-hidden [&_.ql-toolbar]:bg-[#0A0C10] [&_.ql-toolbar]:border-white/10 [&_.ql-container]:bg-[#0A0C10] [&_.ql-container]:border-white/10 [&_.ql-editor]:text-white [&_.ql-editor]:min-h-[120px] [&_.ql-snow_.ql-stroke]:stroke-[#A1A9B8] [&_.ql-snow_.ql-fill]:fill-[#A1A9B8] [&_.ql-snow_.ql-picker-label]:text-[#A1A9B8] [&_.ql-snow_.ql-picker-options]:bg-[#11141A] [&_.ql-snow_.ql-picker-options]:border-white/10" data-testid="welcome-message-editor">
          <RichTextEditor
            value={welcomeMessage}
            onChange={setWelcomeMessage}
            placeholder="Write a welcome message for learners in this cohort..."
            testId="welcome-message-quill"
          />
        </div>
      </div>

      {/* Pre-training LEXPs */}
      <div className="space-y-3">
        <Label className="text-[#A1A9B8] font-body text-sm">Pre-Training Learning Experiences</Label>

        {/* Add LEXP selector */}
        <div className="flex gap-2">
          <Select value={selectedLexpId} onValueChange={setSelectedLexpId}>
            <SelectTrigger className="bg-[#0A0C10] border-white/10 text-white flex-1 h-9 text-xs" data-testid="welcome-lexp-select">
              <SelectValue placeholder="Select a learning experience..." />
            </SelectTrigger>
            <SelectContent>
              {availableLexps.map((l) => (
                <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>
              ))}
              {availableLexps.length === 0 && (
                <div className="px-3 py-2 text-xs text-[#6E7787]">No available LEXPs</div>
              )}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            onClick={addLexp}
            disabled={!selectedLexpId}
            data-testid="welcome-add-lexp-btn"
            className="h-9 px-3 font-body text-white text-xs"
            style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />Add
          </Button>
        </div>

        {/* LEXP list */}
        {welcomeLexps.length === 0 ? (
          <div className="bg-[#0A0C10] border border-dashed border-white/10 rounded-lg p-6 text-center">
            <BookOpen className="h-8 w-8 text-[#6E7787] mx-auto mb-2" />
            <p className="text-[#6E7787] text-xs font-body">No pre-training LEXPs configured</p>
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="welcome-lexp-list">
            {welcomeLexps.map((lexp, idx) => (
              <div
                key={lexp.lexp_id}
                data-testid={`welcome-lexp-item-${lexp.lexp_id}`}
                className="flex items-center gap-2 bg-[#0A0C10] border border-white/10 rounded-lg px-3 py-2.5"
              >
                <span className="text-xs text-[#6E7787] font-body w-5 text-center">{idx + 1}.</span>
                <span className="flex-1 text-sm font-body text-white truncate">{lexp.title}</span>
                <div className="flex items-center gap-0.5">
                  <button
                    onClick={() => moveUp(idx)}
                    disabled={idx === 0}
                    className="p-1 text-[#6E7787] hover:text-white disabled:opacity-30 transition-colors"
                    data-testid={`welcome-move-up-${idx}`}
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => moveDown(idx)}
                    disabled={idx === welcomeLexps.length - 1}
                    className="p-1 text-[#6E7787] hover:text-white disabled:opacity-30 transition-colors"
                    data-testid={`welcome-move-down-${idx}`}
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => removeLexp(lexp.lexp_id)}
                    className="p-1 text-red-400 hover:text-red-300 transition-colors ml-1"
                    data-testid={`welcome-remove-${lexp.lexp_id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Save */}
      <Button
        onClick={handleSave}
        disabled={saving}
        data-testid="welcome-save-btn"
        className="font-body text-white text-sm"
        style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
        Save Welcome Settings
      </Button>
    </div>
  );
}
